import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import PageHeader from '../../components/PageHeader';
import Footer from '../../components/Footer';
import './FAQ.css';

function FAQPage() {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});

  const currentLang = i18n.language || 'bs';
  const prefix = currentLang === 'bs' ? '' : `/${currentLang}`;

  // Retrieve raw FAQ items translated for the active language
  const faqItems = useMemo(() => {
    const raw = t('faqPage.items', { returnObjects: true });
    if (Array.isArray(raw)) {
      return raw;
    }
    return [];
  }, [t, currentLang]);

  // Filter items by category and search term
  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return faqItems.filter(item => {
      const matchCategory =
        activeCategory === 'all' || item.category === activeCategory;

      if (!matchCategory) return false;
      if (!query) return true;

      const questionMatch = item.question?.toLowerCase().includes(query);
      const answerMatch = item.answer?.toLowerCase().includes(query);

      return questionMatch || answerMatch;
    });
  }, [faqItems, activeCategory, searchQuery]);

  const toggleItem = id => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleExpandAll = () => {
    const allOpen = {};
    filteredItems.forEach(item => {
      allOpen[item.id] = true;
    });
    setOpenItems(allOpen);
  };

  const handleCollapseAll = () => {
    setOpenItems({});
  };

  const getCategoryLabel = category => {
    switch (category) {
      case 'general':
        return t('faqPage.filter_general');
      case 'puppies':
        return t('faqPage.filter_puppies');
      case 'costs':
        return t('faqPage.filter_costs');
      case 'cats':
        return t('faqPage.filter_cats');
      default:
        return category;
    }
  };

  const getCategoryIcon = category => {
    switch (category) {
      case 'general':
        return 'fa-paw';
      case 'puppies':
        return 'fa-shield-alt';
      case 'costs':
        return 'fa-dollar-sign';
      case 'cats':
        return 'fa-paw';
      default:
        return 'fa-question-circle';
    }
  };

  return (
    <div id="top">
      <PageHeader
        title={t('faqPage.title')}
        page={t('faqPage.page')}
        subPage={t('faqPage.subPage')}
        pageUrl={'adoption'}
      />

      <div className="page faq-page-container">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 offset-lg-1">
              
              {/* Intro Banner */}
              <div className="faq-intro-card" data-aos="fade-up">
                <div className="faq-intro-header">
                  <div className="faq-intro-icon">
                    <i className="fas fa-clipboard"></i>
                  </div>
                  <div>
                    <h2>{t('faqPage.header_title')}</h2>
                  </div>
                </div>
                <p>{t('faqPage.header_subtitle')}</p>
              </div>

              {/* Quick Highlight Cards */}
              <div className="faq-key-points" data-aos="fade-up" data-aos-delay="100">
                <div className="faq-key-point-item">
                  <div className="faq-key-point-icon icon-behavior">
                    <i className="fas fa-heart"></i>
                  </div>
                  <div className="faq-key-point-text">
                    <h6>{t('faqPage.highlight_socialization_title')}</h6>
                    <p>{t('faqPage.highlight_socialization_desc')}</p>
                  </div>
                </div>

                <div className="faq-key-point-item">
                  <div className="faq-key-point-icon icon-puppy">
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <div className="faq-key-point-text">
                    <h6>{t('faqPage.highlight_puppies_title')}</h6>
                    <p>{t('faqPage.highlight_puppies_desc')}</p>
                  </div>
                </div>

                <div className="faq-key-point-item">
                  <div className="faq-key-point-icon icon-cost">
                    <i className="fas fa-money-bill-alt"></i>
                  </div>
                  <div className="faq-key-point-text">
                    <h6>{t('faqPage.highlight_boarding_title')}</h6>
                    <p>{t('faqPage.highlight_boarding_desc')}</p>
                  </div>
                </div>

                <div className="faq-key-point-item">
                  <div className="faq-key-point-icon icon-cats">
                    <i className="fas fa-paw"></i>
                  </div>
                  <div className="faq-key-point-text">
                    <h6>{t('faqPage.highlight_cats_title')}</h6>
                    <p>{t('faqPage.highlight_cats_desc')}</p>
                  </div>
                </div>
              </div>

              {/* Controls: Search and Filters */}
              <div className="faq-controls-container" data-aos="fade-up" data-aos-delay="150">
                <div className="faq-search-wrapper">
                  <i className="fas fa-search faq-search-icon"></i>
                  <input
                    type="text"
                    className="faq-search-input"
                    placeholder={t('faqPage.search_placeholder')}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="faq-search-clear"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>

                <div className="faq-filter-pills">
                  <div className="faq-category-buttons">
                    <button
                      className={`faq-filter-btn ${activeCategory === 'all' ? 'active' : ''}`}
                      onClick={() => setActiveCategory('all')}
                    >
                      <i className="fas fa-list"></i>
                      {t('faqPage.all_categories')}
                    </button>
                    <button
                      className={`faq-filter-btn ${activeCategory === 'general' ? 'active' : ''}`}
                      onClick={() => setActiveCategory('general')}
                    >
                      <i className="fas fa-paw"></i>
                      {t('faqPage.filter_general')}
                    </button>
                    <button
                      className={`faq-filter-btn ${activeCategory === 'puppies' ? 'active' : ''}`}
                      onClick={() => setActiveCategory('puppies')}
                    >
                    <i className="fas fa-shield-alt"></i>
                      {t('faqPage.filter_puppies')}
                    </button>
                    <button
                      className={`faq-filter-btn ${activeCategory === 'costs' ? 'active' : ''}`}
                      onClick={() => setActiveCategory('costs')}
                    >
                      <i className="fas fa-dollar-sign"></i>
                      {t('faqPage.filter_costs')}
                    </button>
                    <button
                      className={`faq-filter-btn ${activeCategory === 'cats' ? 'active' : ''}`}
                      onClick={() => setActiveCategory('cats')}
                    >
                      <i className="fas fa-paw"></i>
                      {t('faqPage.filter_cats')}
                    </button>
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      className="faq-toggle-all-btn"
                      onClick={handleExpandAll}
                    >
                      {t('faqPage.expand_all')}
                    </button>
                    <button
                      className="faq-toggle-all-btn"
                      onClick={handleCollapseAll}
                    >
                      {t('faqPage.collapse_all')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Accordion FAQ list */}
              {filteredItems.length === 0 ? (
                <div className="faq-empty-state" data-aos="fade-up">
                  <div className="faq-empty-icon">
                    <i className="far fa-question-circle"></i>
                  </div>
                  <h5>{t('faqPage.no_results')}</h5>
                </div>
              ) : (
                <div className="faq-accordion-list" data-aos="fade-up" data-aos-delay="200">
                  {filteredItems.map(item => {
                    const isOpen = !!openItems[item.id];
                    return (
                      <div
                        key={item.id}
                        className={`faq-item-card ${isOpen ? 'is-open' : ''}`}
                      >
                        <button
                          className="faq-item-header"
                          onClick={() => toggleItem(item.id)}
                          aria-expanded={isOpen}
                        >
                          <div className="faq-item-header-left">
                            <span className={`faq-category-tag ${item.category}`}>
                              <i className={`fas ${getCategoryIcon(item.category)} me-1`}></i>
                              {getCategoryLabel(item.category)}
                            </span>
                            <h5 className="faq-item-question">{item.question}</h5>
                          </div>
                          <span className="faq-item-icon-toggle">
                            <i className="fas fa-chevron-down"></i>
                          </span>
                        </button>

                        {isOpen && (
                          <div className="faq-item-body">
                            <ReactMarkdown
                              components={{
                                a: ({ node, ...props }) => (
                                  <a
                                    {...props}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  />
                                ),
                              }}
                            >
                              {item.answer}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* CTA Banner */}
              <div className="faq-cta-banner" data-aos="fade-up">
                <div className="faq-cta-content">
                  <div className="faq-cta-text">
                    <h3>{t('faqPage.cta_title')}</h3>
                    <p>{t('faqPage.cta_desc')}</p>
                  </div>
                  <div className="faq-cta-buttons">
                    <a
                      href="mailto:pawfriendssarajevo@gmail.com?subject=Upit%20za%20prijem%20psa%20-%20PawFriends"
                      className="faq-btn-primary"
                    >
                      <i className="fas fa-envelope"></i>
                      {t('faqPage.cta_btn_email')}
                    </a>
                    <Link
                      to={`${prefix}/adoption-form`}
                      className="faq-btn-secondary"
                    >
                      <i className="fas fa-file-alt"></i>
                      {t('faqPage.cta_btn_form')}
                    </Link>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default FAQPage;
